// src/pages/AdminEditProduct.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../services/api';
import '../styles/AdminAddProduct.css'; 

const AdminEditProduct = () => {
  const { id } = useParams(); 
  const navigate = useNavigate();
  
  // Lookups Data
  const [vendors, setVendors] = useState([]);
  const [dbCategories, setDbCategories] = useState([]); 
  const [availableSubCats, setAvailableSubCats] = useState([]);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true); 
  const [imageLoading, setImageLoading] = useState(false);

  // States
  const [vendorId, setVendorId] = useState('');
  const [basic, setBasic] = useState({ name: '', category: '', subCategory: '', brand: '', shortDescription: '' });
  const [details, setDetails] = useState({ highlights: '', additionalInfo: '' });
  const [policy, setPolicy] = useState({ isRefundable: false, isExchangeable: false, returnDays: '' });
  
  // Base Product States (If Simple Product)
  const [pricing, setPricing] = useState({ mrp: '', sellingPrice: '' });
  const [inventory, setInventory] = useState({ stockQuantity: '10', sku: '', barcode: '' });
  const [images, setImages] = useState([]); 
  const [globalAttributes, setGlobalAttributes] = useState({});

  // 🔥 THE VARIANT ENGINE STATES (Admin Version) 🔥
  const [hasVariants, setHasVariants] = useState(false);
  const [variants, setVariants] = useState([]);
  const [matrixInputs, setMatrixInputs] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // 1. Fetch Lookups (Vendors + Categories + Current Product)
        const [vendorRes, catRes, productRes] = await Promise.all([
          apiClient.get('/admin/vendors/approved', { headers }),
          apiClient.get('/categories/all'),
          apiClient.get(`/products/${id}`, { headers })
        ]);

        const allVendors = vendorRes.data.vendors || [];
        const allCats = catRes.data.categories || [];
        setVendors(allVendors);
        setDbCategories(allCats);

        // 2. Setup Product Details
        const p = productRes.data.product;
        if (p) {
          setVendorId(p.vendorId?._id || p.vendorId || '');
          setBasic({ 
            name: p.name || '', category: p.category || '', subCategory: p.subCategory || '', 
            brand: p.brand || '', shortDescription: p.shortDescription || '' 
          });
          setDetails({ 
            highlights: p.highlights ? p.highlights.join('\n') : '', 
            additionalInfo: p.additionalInfo || '' 
          });
          setPolicy({ 
            isRefundable: p.returnPolicy?.isRefundable || false, 
            isExchangeable: p.returnPolicy?.isExchangeable || false, 
            returnDays: p.returnPolicy?.returnDays || '' 
          });
          
          setPricing({ mrp: p.pricing?.mrp || '', sellingPrice: p.pricing?.sellingPrice || '' });
          setInventory({ stockQuantity: p.inventory?.stockQuantity || '0', sku: p.inventory?.sku || '', barcode: p.inventory?.barcode || '' });
          setImages(p.images || []);
          setGlobalAttributes(p.globalAttributes || p.attributes || {});

          // 🔥 Load Variants Info 🔥
          setHasVariants(p.hasVariants || false);
          if (p.hasVariants && p.variants) {
            const formattedVariants = p.variants.map(v => ({
              attributes: v.attributes || {},
              pricing: { mrp: v.pricing?.mrp?.toString() || '', sellingPrice: v.pricing?.sellingPrice?.toString() || '' },
              inventory: { stockQuantity: v.inventory?.stockQuantity?.toString() || '0', sku: v.inventory?.sku || '' },
              image: v.images && v.images.length > 0 ? v.images[0] : (v.image || null)
            }));
            setVariants(formattedVariants);
          }

          // Sub-cat dropdown logic based on loaded product
          const selectedCatObj = allCats.find(c => c.name === p.category);
          if (selectedCatObj) setAvailableSubCats(selectedCatObj.subCategories || []);
        }
      } catch (error) { 
        alert("Failed to load data."); 
        console.error(error);
      } 
      finally { setPageLoading(false); }
    };
    fetchData();
  }, [id]);

  // Category Logic
  const handleCategoryChange = (e) => {
    const catName = e.target.value;
    setBasic({...basic, category: catName, subCategory: ''});
    setGlobalAttributes({}); setVariants([]); setMatrixInputs({});
    
    const selectedCatObj = dbCategories.find(c => c.name === catName);
    if (selectedCatObj) setAvailableSubCats(selectedCatObj.subCategories || []);
    else setAvailableSubCats([]);
  };

  const handleSubCategoryChange = (e) => {
    const subCatName = e.target.value;
    setBasic({...basic, subCategory: subCatName});
    setGlobalAttributes({}); setVariants([]); setMatrixInputs({});
  };

  const currentSubCatObj = availableSubCats.find(s => s.name === basic.subCategory);
  const activeAttributes = currentSubCatObj ? (currentSubCatObj.attributes || []) : [];

  // 🔥 IMAGE UPLOAD LOGIC (Base or Variant Single Image) 🔥
  const handleImageUpload = async (e, variantIndex = -1) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (variantIndex === -1 && images.length + files.length > 4) { 
      alert("Maximum 4 base images allowed!"); return; 
    }

    setImageLoading(true);
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      const filesToUpload = variantIndex === -1 ? files : [files[0]];

      for (let file of filesToUpload) {
        const formData = new FormData(); formData.append('image', file); 
        const uploadRes = await apiClient.post('/vendor/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
        });
        
        if (uploadRes.data && uploadRes.data.imageUrl) {
          if (variantIndex === -1) {
            setImages(prev => [...prev, uploadRes.data.imageUrl]);
          } else {
            const newVariants = [...variants];
            newVariants[variantIndex].image = uploadRes.data.imageUrl; // Single String
            setVariants(newVariants);
          }
        }
      }
    } catch (error) { alert("Upload Failed."); } 
    finally { setImageLoading(false); }
  };

  const removeBaseImage = (index) => setImages(images.filter((_, i) => i !== index));
  const removeVariantImage = (index) => {
    const newVariants = [...variants];
    newVariants[index].image = null;
    setVariants(newVariants);
  };

  // 🔥 MATRIX GENERATOR ENGINE 🔥
  const generateMatrixVariants = () => {
    let combinations = [{}];
    let hasValues = false;

    activeAttributes.forEach(attr => {
      if(matrixInputs[attr] && matrixInputs[attr].trim() !== '') {
        hasValues = true;
        const values = matrixInputs[attr].split(',').map(v => v.trim()).filter(v => v !== '');
        const temp = [];
        combinations.forEach(combo => {
          values.forEach(val => { temp.push({...combo, [attr]: val}); });
        });
        combinations = temp;
      }
    });

    if(!hasValues) {
      alert("Please enter comma-separated values to generate matrix (e.g., S, M, L).");
      return;
    }

    const autoVariants = combinations.map(combo => ({
      attributes: combo,
      pricing: { mrp: pricing.mrp || '', sellingPrice: pricing.sellingPrice || '' },
      inventory: { stockQuantity: '10', sku: '' },
      image: null
    }));

    setVariants([...variants, ...autoVariants]);
    setMatrixInputs({}); 
  };

  const addManualVariant = () => {
    setVariants([...variants, { attributes: {}, pricing: { mrp: pricing.mrp || '', sellingPrice: pricing.sellingPrice || '' }, inventory: { stockQuantity: '10', sku: '' }, image: null }]);
  };
  const removeVariant = (index) => setVariants(variants.filter((_, i) => i !== index));
  const updateVariant = (index, field, subfield, value) => {
    const newVariants = [...variants];
    newVariants[index][field][subfield] = value;
    setVariants(newVariants);
  };

  // 🔥 SUBMIT HANDLER 🔥
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!vendorId || !basic.name || !basic.category) {
      alert("Vendor, Name and Category are required!"); return;
    }
    if (!hasVariants && (!pricing.mrp || !pricing.sellingPrice)) {
      alert("MRP and Selling Price are required for Simple Product!"); return;
    }
    if (hasVariants && variants.length === 0) {
      alert("Please generate or add at least one variant."); return;
    }

    setLoading(true);
    try {
      const highlightsArray = details.highlights ? details.highlights.split('\n').filter(h => h.trim() !== '') : [];
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

      const payload = {
        vendorId, ...basic, highlights: highlightsArray, additionalInfo: details.additionalInfo,
        returnPolicy: { isRefundable: policy.isRefundable, isExchangeable: policy.isExchangeable, returnDays: Number(policy.returnDays) || 0 },
        hasVariants,
        globalAttributes,
        images
      };

      if (!hasVariants) {
        payload.pricing = { mrp: Number(pricing.mrp), sellingPrice: Number(pricing.sellingPrice) };
        payload.inventory = { stockQuantity: Number(inventory.stockQuantity), sku: inventory.sku, barcode: inventory.barcode };
      } else {
        payload.variants = variants.map(v => ({
          ...v,
          pricing: { mrp: Number(v.pricing.mrp), sellingPrice: Number(v.pricing.sellingPrice) },
          inventory: { stockQuantity: Number(v.inventory.stockQuantity), sku: v.inventory.sku },
          images: v.image ? [v.image] : [] // Backend expects array
        }));
      }

      await apiClient.put(`/products/admin/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      alert("Product updated successfully!"); navigate('/dashboard/products'); 
    } catch (error) { alert("Error updating product."); } 
    finally { setLoading(false); }
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://pickupdrop.dodigitals.in${url.startsWith('/') ? url : `/${url}`}`;
  };

  if (pageLoading) return <div style={{ padding: '50px', textAlign: 'center' }}>Loading Product Data...</div>;

  return (
    <div className="add-product-container">
      <div className="admin-header">
        <div><h2 className="admin-title">Edit Product (Admin)</h2></div>
        <button type="button" className="btn-edit" onClick={() => navigate('/dashboard/products')} style={{background: '#F1F5F9', color: '#475569', border: 'none'}}>Cancel & Go Back</button>
      </div>

      <form onSubmit={handleSubmit}>
        
        {/* 1. VENDOR ASSIGNMENT */}
        <div className="form-card" style={{ borderLeft: '4px solid #ea6724' }}>
          <h3 className="card-title">🏪 Assign to Store / Vendor</h3>
          <div className="form-group">
            <label className="form-label">Select Vendor *</label>
            <select className="form-select" value={vendorId} onChange={(e) => setVendorId(e.target.value)} required>
              <option value="" disabled>-- Choose a Store --</option>
              {vendors.map(v => <option key={v._id} value={v._id}>{v.shopName} ({v.ownerName})</option>)}
            </select>
          </div>
        </div>

        {/* 2. BASIC DETAILS */}
        <div className="form-card">
          <h3 className="card-title">📦 Basic Details</h3>
          <div className="form-group">
            <label className="form-label">Product Name *</label>
            <input type="text" className="form-input" value={basic.name} onChange={(e) => setBasic({...basic, name: e.target.value})} required />
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Main Category *</label>
              <select className="form-select" value={basic.category} onChange={handleCategoryChange} required>
                <option value="" disabled>-- Select Category --</option>
                {dbCategories.map(cat => <option key={cat._id} value={cat.name}>{cat.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Sub-Category</label>
              {availableSubCats.length > 0 ? (
                <select className="form-select" value={basic.subCategory} onChange={handleSubCategoryChange}>
                  <option value="">-- Select Sub-Category --</option>
                  {availableSubCats.map(sub => <option key={sub._id} value={sub.name}>{sub.name}</option>)}
                </select>
              ) : (
                <input type="text" className="form-input" value={basic.subCategory} onChange={handleSubCategoryChange} />
              )}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group"><label className="form-label">Brand</label><input type="text" className="form-input" value={basic.brand} onChange={(e) => setBasic({...basic, brand: e.target.value})} /></div>
          </div>
          <div className="form-group"><label className="form-label">Short Description</label><textarea className="form-textarea" value={basic.shortDescription} onChange={(e) => setBasic({...basic, shortDescription: e.target.value})} /></div>
        </div>

        {/* 🔥 VARIANT SWITCH 🔥 */}
        {basic.subCategory !== '' && (
          <div className="form-card" style={{ background: '#FFF0E6', border: '1px solid #FFD8C4' }}>
            <h3 className="card-title" style={{ color: '#ea6724' }}>🎛️ Product Variations</h3>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <label className="form-label" style={{ marginBottom: 0 }}>Does it have variants?</label>
                <p style={{ fontSize: '12px', color: '#64748B', margin: 0 }}>Sizes, Colors, Storage, etc.</p>
              </div>
              <input type="checkbox" style={{ width: '24px', height: '24px', accentColor: '#ea6724' }} checked={hasVariants} onChange={(e) => setHasVariants(e.target.checked)} />
            </div>
          </div>
        )}

        {/* 🔥 IF NO VARIANTS -> SHOW SIMPLE PRICING & ATTRS 🔥 */}
        {!hasVariants && basic.subCategory !== '' && (
          <div className="form-card">
            <h3 className="card-title">💰 Pricing & Inventory</h3>
            <div className="form-grid-2">
              <div className="form-group"><label className="form-label">MRP (₹) *</label><input type="number" className="form-input" value={pricing.mrp} onChange={(e) => setPricing({...pricing, mrp: e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Selling Price (₹) *</label><input type="number" className="form-input" value={pricing.sellingPrice} onChange={(e) => setPricing({...pricing, sellingPrice: e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Stock Quantity</label><input type="number" className="form-input" value={inventory.stockQuantity} onChange={(e) => setInventory({...inventory, stockQuantity: e.target.value})} /></div>
              <div className="form-group"><label className="form-label">Barcode / UPC</label><input type="text" className="form-input" value={inventory.barcode} onChange={(e) => setInventory({...inventory, barcode: e.target.value})} /></div>
            </div>

            {activeAttributes.length > 0 && (
              <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #E2E8F0' }}>
                <h4 style={{ fontSize: '14px', marginBottom: '15px' }}>Attributes (Optional)</h4>
                <div className="form-grid-2">
                  {activeAttributes.map(attr => (
                    <div key={attr} className="form-group">
                      <label className="form-label">{attr}</label>
                      {attr.toLowerCase().includes('veg/non') ? (
                        <select className="form-select" value={globalAttributes[attr] || ''} onChange={(e) => setGlobalAttributes({...globalAttributes, [attr]: e.target.value})}>
                          <option value="">Select...</option><option value="Veg">Veg</option><option value="Non-Veg">Non-Veg</option><option value="N/A">N/A</option>
                        </select>
                      ) : (
                        <input type="text" className="form-input" placeholder={`e.g. Value`} value={globalAttributes[attr] || ''} onChange={(e) => setGlobalAttributes({...globalAttributes, [attr]: e.target.value})} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 🔥 IF HAS VARIANTS -> MATRIX GENERATOR 🔥 */}
        {hasVariants && activeAttributes.length > 0 && (
          <div className="form-card" style={{ background: '#EEF2FF', border: '1px solid #C7D2FE' }}>
            <h3 className="card-title" style={{ color: '#4F46E5' }}>⚡ Matrix Generator</h3>
            <p style={{ fontSize: '13px', color: '#64748B', marginBottom: '15px' }}>Type comma separated values to auto-generate variants (e.g. Red, Black).</p>
            
            <div className="form-grid-2">
              {activeAttributes.map(attr => (
                <div key={attr} className="form-group">
                  <label className="form-label" style={{ color: '#4F46E5' }}>{attr} Options</label>
                  <input 
                    type="text" className="form-input" 
                    placeholder={`e.g. ${attr === 'Size' ? 'S, M, L' : 'Value1, Value2'}`} 
                    value={matrixInputs[attr] || ''} 
                    onChange={(e) => setMatrixInputs({...matrixInputs, [attr]: e.target.value})} 
                  />
                </div>
              ))}
            </div>
            
            <button type="button" onClick={generateMatrixVariants} style={{ background: '#4F46E5', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', width: '100%', marginTop: '10px' }}>
              ⚡ Generate Combinations
            </button>
          </div>
        )}

        {/* 🔥 VARIANTS LIST 🔥 */}
        {hasVariants && (
          <div className="form-card" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
            <h3 className="card-title">🗂️ Managed Variants ({variants.length})</h3>
            
            {variants.length === 0 && <p style={{ color: '#94A3B8', fontStyle: 'italic', textAlign: 'center' }}>Use Matrix Generator above or add manually.</p>}

            {variants.map((variant, vIndex) => (
              <div key={vIndex} style={{ background: '#fff', border: '1px solid #E2E8F0', padding: '20px', borderRadius: '12px', marginBottom: '20px', position: 'relative' }}>
                <button type="button" onClick={() => removeVariant(vIndex)} style={{ position: 'absolute', top: '15px', right: '15px', background: '#FEE2E2', color: '#EF4444', border: 'none', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer', fontWeight: 'bold' }}>Delete</button>
                
                <h4 style={{ marginBottom: '15px', color: '#355ca9' }}>Variant #{vIndex + 1}</h4>
                
                {/* Variant Attributes */}
                <div className="form-grid-2" style={{ marginBottom: '15px' }}>
                  {activeAttributes.map(attr => (
                    <div key={attr} className="form-group" style={{ marginBottom: '10px' }}>
                      <label className="form-label" style={{ fontSize: '12px' }}>{attr}</label>
                      <input type="text" className="form-input" value={variant.attributes[attr] || ''} onChange={(e) => updateVariant(vIndex, 'attributes', attr, e.target.value)} />
                    </div>
                  ))}
                </div>

                {/* Variant Pricing & Stock */}
                <div style={{ display: 'flex', gap: '15px', marginBottom: '15px', flexWrap: 'wrap' }}>
                  <div className="form-group" style={{ flex: '1 1 100px' }}><label className="form-label" style={{ fontSize: '12px' }}>MRP (₹)</label><input type="number" className="form-input" value={variant.pricing.mrp} onChange={(e) => updateVariant(vIndex, 'pricing', 'mrp', e.target.value)} /></div>
                  <div className="form-group" style={{ flex: '1 1 100px' }}><label className="form-label" style={{ fontSize: '12px' }}>Selling Price (₹)</label><input type="number" className="form-input" value={variant.pricing.sellingPrice} onChange={(e) => updateVariant(vIndex, 'pricing', 'sellingPrice', e.target.value)} style={{ borderColor: '#10B981' }} /></div>
                  <div className="form-group" style={{ flex: '1 1 100px' }}><label className="form-label" style={{ fontSize: '12px' }}>Stock</label><input type="number" className="form-input" value={variant.inventory.stockQuantity} onChange={(e) => updateVariant(vIndex, 'inventory', 'stockQuantity', e.target.value)} /></div>
                </div>

                {/* Variant Image (Single) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  {variant.image && (
                    <div style={{ position: 'relative' }}>
                      <img src={getFullImageUrl(variant.image)} alt="var" style={{ width: '60px', height: '60px', borderRadius: '8px', border: '1px solid #ccc', objectFit: 'cover' }} />
                      <button type="button" onClick={() => removeVariantImage(vIndex)} style={{ position: 'absolute', top: '-5px', right: '-5px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer' }}>×</button>
                    </div>
                  )}
                  <label style={{ background: '#EEF2FF', color: '#4F46E5', padding: '8px 15px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', border: '1px solid #C7D2FE' }}>
                    {variant.image ? 'Change Image' : 'Upload Specific Image'}
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleImageUpload(e, vIndex)} />
                  </label>
                </div>
              </div>
            ))}

            <button type="button" onClick={addManualVariant} style={{ width: '100%', padding: '12px', background: '#F1F5F9', border: '2px dashed #94A3B8', borderRadius: '8px', color: '#475569', fontWeight: 'bold', cursor: 'pointer' }}>
              + Add Blank Variant
            </button>
          </div>
        )}

        {/* HIGHLIGHTS & RETURN */}
        <div className="form-card">
          <h3 className="card-title">⭐ Highlights & Info</h3>
          <div className="form-group"><label className="form-label">Key Highlights (Press Enter for new point)</label><textarea className="form-textarea" value={details.highlights} onChange={(e) => setDetails({...details, highlights: e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Additional Info</label><textarea className="form-textarea" value={details.additionalInfo} onChange={(e) => setDetails({...details, additionalInfo: e.target.value})} /></div>
        </div>

        <div className="form-card">
          <h3 className="card-title">🔄 Return Policy</h3>
          <div className="form-grid-2">
            <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px' }}><input type="checkbox" style={{ width: '20px', height: '20px', accentColor: '#ea6724' }} checked={policy.isRefundable} onChange={(e) => setPolicy({...policy, isRefundable: e.target.checked})} /><label className="form-label" style={{ marginBottom: 0 }}>Is Refundable?</label></div>
            <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px' }}><input type="checkbox" style={{ width: '20px', height: '20px', accentColor: '#ea6724' }} checked={policy.isExchangeable} onChange={(e) => setPolicy({...policy, isExchangeable: e.target.checked})} /><label className="form-label" style={{ marginBottom: 0 }}>Is Exchangeable?</label></div>
          </div>
          {(policy.isRefundable || policy.isExchangeable) && (
            <div className="form-group" style={{ marginTop: '15px' }}><label className="form-label">Return/Exchange Window (in Days)</label><input type="number" className="form-input" value={policy.returnDays} onChange={(e) => setPolicy({...policy, returnDays: e.target.value})} /></div>
          )}
        </div>

        {/* BASE IMAGES */}
        <div className="form-card">
          <h3 className="card-title">📸 Global / Base Images</h3>
          <label className="image-upload-area">
            {imageLoading ? <span style={{ color: '#ea6724', fontWeight: 'bold' }}>Uploading...</span> : <span>Click here to select images (Max 4)</span>}
            <input type="file" multiple accept="image/jpeg, image/png, image/webp" onChange={(e) => handleImageUpload(e, -1)} style={{ display: 'none' }} disabled={imageLoading} />
          </label>
          <div className="image-preview-grid" style={{ display: 'flex', gap: '15px', marginTop: '20px', flexWrap: 'wrap' }}>
            {images.map((url, idx) => (
              <div key={idx} className="image-preview-box" style={{ width: '100px', height: '100px', borderRadius: '8px', position: 'relative', border: '1px solid #E2E8F0' }}>
                <img src={getFullImageUrl(url)} alt="Prev" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                <button type="button" className="remove-img-btn" onClick={() => removeBaseImage(idx)} style={{ position: 'absolute', top: '5px', right: '5px', background: '#DC2626', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer' }}>×</button>
              </div>
            ))}
          </div>
        </div>

        <button type="submit" className="submit-btn" disabled={loading || imageLoading}>
          {loading ? "Updating..." : "Update Product"}
        </button>

      </form>
    </div>
  );
};

export default AdminEditProduct;